import { useRef, useState } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { CourseOption, ClassSection } from "../../types/course.response";

interface CourseCardForSelectingProps {
    courseOption: CourseOption;
    semester: string;
    color: string;
    onDeleteCourse: (course: CourseOption) => void;
    onSectionSelect: (course: CourseOption, type: string, sectionNbr: string) => void;
    onToggleScheduled: (course: CourseOption) => void;
    isHighlighted?: boolean;
    isDimmed?: boolean;
}

export default function CourseCardForSelecting(
    {
        courseOption,
        semester,
        color,
        onDeleteCourse,
        onSectionSelect,
        onToggleScheduled,
        isHighlighted,
        isDimmed,
    }: CourseCardForSelectingProps
) {
  const [isExpanded, setIsExpanded] = useState(false);

  const isOnSchedule = courseOption.userState.isScheduled;

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleScheduled(courseOption);
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDeleteCourse = () => {
    onDeleteCourse(courseOption);
  };
  
  const getSectionsByType = () => {
    const sectionsByType: Record<string, ClassSection[]> = {};
    
    courseOption.enrollGroups
      .filter(group => group.semester === semester)
      .forEach(group => {
        const currentSections = group.classSections.filter(section => 
          section.meetings.length > 0 && section.meetings[0].timeStart !== ""
        );
        
        currentSections.forEach(section => {
          if (section.sectionType && !sectionsByType[section.sectionType]) {
            sectionsByType[section.sectionType!] = [];
          }
          sectionsByType[section.sectionType!].push(section);
        });
      });
    
    return sectionsByType;
  };
  
  const handleSectionSelect = (course: CourseOption, type: string, sectionNumber: string) => {
    onSectionSelect(course, type, sectionNumber);
  };
  
  const renderStatusDot = (section: ClassSection) => {
    if (section.openStatus === "C") {
      return <div className="w-3 h-3 bg-red-700 rounded-sm flex-shrink-0" />;
    }
    if (section.openStatus === "W") {
      return <div className="w-3 h-3 bg-orange-400 rounded-full flex-shrink-0" />;
    }
    return <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0" />;
  };
  
  const sectionsByType = getSectionsByType();

  const renderCourseAvailable = () => {
    return (

    <div className={`mb-3 rounded-md shadow-sm transition-opacity ${isHighlighted ? 'ring-2 ring-blue-400 border-2 border-blue-300' : 'border border-gray-200'} ${isDimmed ? 'opacity-35' : ''}`}>
        {/* 卡片标题栏 */}
        <div 
        className={`px-3 py-1 flex justify-between items-center cursor-pointer ${isExpanded ? 'rounded-t-md' : 'rounded-md'}`}
        style={{ backgroundColor: color || '#cbd5e1' }}
        onClick={handleToggleExpand}
        >
        <div className="flex items-center gap-3">
            {/* Checkbox */}
            <button
              onClick={handleCheckboxClick}
              className="flex-shrink-0 hover:opacity-80 transition-opacity"
            >
              <div className="w-5 h-5 flex items-center justify-center rounded border-2 border-white bg-transparent">
                {isOnSchedule && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>
            
            <div className="flex flex-col">
                <span className="text-white text-[.8rem] font-bold">{courseOption.courseInfo.subject} {courseOption.courseInfo.number}</span> 
                <span className="text-white text-[.8rem]">{courseOption.courseInfo.titleShort} {courseOption.topic && `- ${courseOption.topic}`}</span>
            </div>
        </div>
        {/* Delete button */}
        <button
            className="rounded-full hover:bg-white/20 p-1 transition-colors"
            onClick={(e) => {
                e.stopPropagation();
                handleDeleteCourse();
            }}
        >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
        </div>
        
        {/* 课程详情和部分选择区 - 只在展开时显示 */}
        {isExpanded && (
        <div className="bg-white px-2 rounded-b-md">
            <span className="text-[.8rem] py-2">{courseOption.enrollGroups[0]?.creditsMinimum} Credits</span>
            
            {/* 按类型分组的部分选择区 */}
            {Object.entries(sectionsByType).map(([type, sections]) => (
            <div key={type} className="my-1.5">
                <div className="">
                {sections.map((section) => {
                    console.log(courseOption.userState);
                    const isSelected = isOnSchedule && courseOption.userState.sectionNumbers.includes(section.sectionNumber);
                    
                    if (!isOnSchedule) {
                        return (
                        <div
                            key={`${type}-${section.sectionNumber}`}
                            className="w-full text-left px-2 py-0.5 rounded"
                        >
                            <div className="flex items-center justify-between">
                            <div className="flex gap-2">
                                <div className="flex justify-between">
                                <span className="text-[.8rem] w-16">{type} {section.sectionNumber}</span>
                                </div>
                                <div className="flex flex-col gap-2">
                                {section.meetings.map((meeting, idx) => (
                                <div key={idx} className="text-[.8rem]">
                                    <div className="flex gap-2">
                                    <span className="w-12">{meeting.pattern}</span>
                                    <span>{meeting.timeStart}</span>
                                    </div>
                                </div>
                                ))}
                                </div>
                            </div>
                            {renderStatusDot(section)}
                            </div>
                        </div>
                        );
                    }
                    
                    return (
                    <button
                        key={`${type}-${section.sectionNumber}`}
                        onClick={() => handleSectionSelect(courseOption, type, section.sectionNumber)}
                        className={`w-full text-left px-2 py-0.5 rounded transition-colors ${
                        isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center justify-between">
                        <div className="flex items-start space-x-3">
                        {/* Blue dot indicator */}
                        <div className="mt-1 flex items-center justify-center w-4 h-4 rounded-full border-2 border-gray-400 flex-shrink-0">
                            {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <div className="flex justify-between">
                            <span className="text-[.8rem] w-16">{type} {section.sectionNumber}</span>
                            </div>
                            <div className="flex flex-col gap-2">
                            {section.meetings.map((meeting, idx) => (
                            <div key={idx} className="text-[.8rem]">
                                <div className="flex gap-2">
                                <span className="w-12">{meeting.pattern}</span>
                                <span>{meeting.timeStart}</span>
                                </div>
                            </div>
                            ))}
                            </div>
                        </div>
                        </div>
                        {renderStatusDot(section)}
                        </div>
                    </button>
                    );
                })}
                </div>
            </div>
            ))}
            
            {/* 底部链接 */}
            <div className="mb-2 flex gap-4 text-[.8rem] text-blue-600">
                <a href={`https://classes.cornell.edu/browse/roster/${semester}/class/${courseOption.courseInfo.subject}/${courseOption.courseInfo.number}`} className="hover:underline">Course Details</a>
            </div>
        </div>
        )}
    </div>
    );
  };

  return (
    <div>
      {renderCourseAvailable()}
    </div>
  );
}
